/**
 * ReasonsKey \u2014 collapsible legend that decodes every reason chip and
 * managed-list name shown in the table beneath it. Sourced from the
 * same definitions as ReasonChips so the legend is always in sync.
 *
 * Mounted above the Investigations events table and the Top High-Risk
 * Events card on the Overview tab.
 */
import { useState } from "react";
import clsx from "clsx";
import {
  REASON_DEFS,
  MANAGED_LIST_DEFS,
  DETECTION_ID_DEFS,
  ENDPOINT_GROUP_DEFS,
  type ReasonDef,
} from "@/lib/reasonCodes";

const TONE_CLASS: Record<ReasonDef["severity"], string> = {
  high:       "border-sev-high/40 bg-sev-high/10 text-sev-high",
  suspicious: "border-sev-suspicious/40 bg-sev-suspicious/10 text-sev-suspicious",
  watch:      "border-sev-watch/40 bg-sev-watch/10 text-sev-watch",
  good:       "border-sev-good/40 bg-sev-good/10 text-sev-good",
  neutral:    "border-border bg-surface-3 text-fg-muted",
};

interface Props {
  /** Optional title override. */
  title?: string;
  /** Default open state (default false). */
  defaultOpen?: boolean;
}

export function ReasonsKey({ title = "Reason key", defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  // Group reason defs by family for a tidier presentation.
  const byFamily = new Map<string, { code: string; def: ReasonDef }[]>();
  for (const [code, def] of Object.entries(REASON_DEFS)) {
    if (code === "detection_id") continue;
    const list = byFamily.get(def.family) ?? [];
    list.push({ code, def });
    byFamily.set(def.family, list);
  }

  return (
    <div className="rounded-lg border border-border bg-surface-2/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-2"
        aria-expanded={open}
      >
        <span className="font-semibold uppercase tracking-wider text-fg-subtle">{title}</span>
        <span className="text-fg-muted">
          {open ? "Hide \u2191" : "What do these mean? \u2193"}
        </span>
      </button>
      {open && (
        <div className="grid gap-4 border-t border-border px-3 py-3 text-xs sm:grid-cols-2">
          {[...byFamily.entries()].map(([family, items]) => (
            <section key={family}>
              <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">{family}</h4>
              <ul className="space-y-1">
                {items.map(({ code, def }) => (
                  <li key={code} className="flex items-start gap-2">
                    <span className={clsx(
                      "inline-flex shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
                      TONE_CLASS[def.severity],
                    )}>{def.short}</span>
                    <span className="text-fg-muted">{def.plain}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <section>
            <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
              Cloudflare alerts
            </h4>
            <ul className="space-y-1">
              {Object.entries(DETECTION_ID_DEFS).map(([id, def]) => (
                <li key={id} className="flex items-start gap-2">
                  <span className={clsx(
                    "inline-flex shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
                    TONE_CLASS.high,
                  )}>Cloudflare alert</span>
                  <span className="text-fg-muted">
                    <span className="font-mono text-fg">{id}</span>{" "}
                    <span className="text-fg">{def.short}</span>: {def.plain}{" "}
                    <span className="font-mono text-sev-high">+{def.weight}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
              Cloudflare threat lists
            </h4>
            <ul className="space-y-1">
              {Object.entries(MANAGED_LIST_DEFS).map(([code, def]) => (
                <li key={code} className="flex items-start gap-2">
                  <span className={clsx(
                    "inline-flex shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
                    TONE_CLASS[def.severity],
                  )}>{def.short}</span>
                  <span className="text-fg-muted">{def.plain}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
              Areas
            </h4>
            <ul className="space-y-1">
              {Object.entries(ENDPOINT_GROUP_DEFS).map(([code, def]) => (
                <li key={code} className="flex items-start gap-2">
                  <span className="inline-flex shrink-0 rounded-md border border-border bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-fg-muted">{def.short}</span>
                  <span className="text-fg-muted">{def.plain}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}
